import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SaveBar, DetailTabPanel } from "@/components/common";
import { preventImplicitSubmitOnEnter } from "@/lib/formKeyboard";
import {
  HouseAdminCommissionSection,
  HouseLocationSection,
  HouseVisibilitySection,
} from "./HouseDetailSections";
import { HouseHeader } from "./HouseHeader";
import { HouseDetailTabNav } from "./HouseDetailTabNav";
import { useHouseDetailTab } from "./useHouseDetailTab";
import { HouseOverviewStats } from "./HouseOverviewStats";
import { HouseAdminDevicesSection } from "./HouseAdminDevicesSection";
import { HousePresencesSection } from "./HousePresencesSection";
import { AdminHousePresenceImportSection } from "./AdminHousePresenceImportSection";
import { useHouseAdminProfileForm } from "./useHouseProfileForm";
import { useAuthStore } from "@/store/authStore";
import { Permission, type HouseWithDeviceCount } from "@/types";

interface HouseAdminProfileFormProps {
  house: HouseWithDeviceCount;
  houseId: string;
  /** Extra header action (e.g. link to the owning partner). */
  headerAction?: ReactNode;
}

/**
 * Staff view of a structure: identity + KPI strip, then devices (default),
 * presences, and the rarely-edited settings (address, host share, visibility).
 */
export function HouseAdminProfileForm({
  house,
  houseId,
  headerAction,
}: HouseAdminProfileFormProps) {
  const { t } = useTranslation("houses");
  const canImportPresences = useAuthStore((s) =>
    s.hasPermission(Permission.HOUSES_PRESENCES_IMPORT),
  );
  const [activeTab, setActiveTab] = useHouseDetailTab(true);

  const {
    formId,
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    nameModified,
    locationModified,
    visibilityModified,
    commissionModified,
    listed,
    setAddressValidation,
    onSubmit,
    handleRevert,
    saveBarPhase,
    confirmModal,
    isPending,
  } = useHouseAdminProfileForm(house, houseId);

  return (
    <div className="w-full min-w-0" onKeyDown={preventImplicitSubmitOnEnter}>
      <form id={formId} className="w-full min-w-0" onSubmit={handleSubmit(onSubmit)}>
        <HouseHeader
          house={house}
          nameInputProps={register("name")}
          nameError={errors.name?.message}
          nameModified={nameModified}
          metrics={
            <HouseOverviewStats
              house={house}
              houseId={houseId}
              showHostShare
              showTelemetry
              onOpenDevices={() => setActiveTab("devices")}
              onOpenGuests={() => setActiveTab("presences")}
            />
          }
          listed={listed}
          action={headerAction}
        />

        <HouseDetailTabNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showPresencesTab
        />

        <DetailTabPanel
          id="house-tabpanel-general"
          labelledBy="house-tab-general"
          isActive={activeTab === "general"}
          className="space-y-5"
        >
          <HouseLocationSection
            value={{
              address: watch("address"),
              coordinates: watch("coordinates"),
            }}
            onChange={(next) => {
              setValue("address", next.address, { shouldDirty: true });
              setValue("coordinates", next.coordinates, { shouldDirty: true });
            }}
            error={errors.address?.message}
            syncKey={`${house.id}-${house.updated_at}`}
            modified={locationModified}
            title={t("adminDetail.locationTitle")}
            onValidationChange={setAddressValidation}
          />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <HouseAdminCommissionSection
              register={register}
              errors={errors}
              modified={commissionModified}
            />
            <HouseVisibilitySection
              inputProps={register("is_active")}
              modified={visibilityModified}
            />
          </div>
        </DetailTabPanel>
      </form>

      <DetailTabPanel
        id="house-tabpanel-devices"
        labelledBy="house-tab-devices"
        isActive={activeTab === "devices"}
      >
        <HouseAdminDevicesSection houseId={houseId} houseName={house.name} />
      </DetailTabPanel>

      <DetailTabPanel
        id="house-tabpanel-presences"
        labelledBy="house-tab-presences"
        isActive={activeTab === "presences"}
        className="space-y-5"
      >
        {canImportPresences ? (
          <AdminHousePresenceImportSection houseId={houseId} />
        ) : null}
        <HousePresencesSection houseId={houseId} deviceCount={house.device_count} />
      </DetailTabPanel>

      <SaveBar
        phase={saveBarPhase}
        isPending={isPending}
        formId={formId}
        onRevert={handleRevert}
      />
      {confirmModal}
    </div>
  );
}
